import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import {
  Card,
  Skeleton,
  Box,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import ChartFilter from "./Filter";
import OrderFilter from "./OrderFilter";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const BusiestHourChart = () => {
  const theme = useTheme();
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);
  const childRef = useRef(null);

  const [filter, setFilter] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const url = `analytics/busiest-hours/by-weekday`;
      const { data } = await axios.get(`${baseURL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...filter },
      });
      setDataList(data.data || []);
    } catch (error) {
      console.error("Error fetching frames:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // ترتيب الأيام
  const weekdaysOrder = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const labels = weekdaysOrder;

  const values = labels.map(day => {
    const found = dataList.find(i => i.weekday_name === day);
    return found ? found.avg_person_count : 0;
  });

  const hours = labels.map(day => {
    const found = dataList.find(i => i.weekday_name === day);
    return found ? `${found.hour}:00` : "-";
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Average Person Count",
        data: values,
        backgroundColor: alpha(theme.palette.primary.main, 0.6),
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", align: "end", labels: { color: theme.palette.text.primary } },
      title: {
        display: true,
        text: "Busiest Hour for Each Weekday (Average Persons)",
        color: theme.palette.text.primary,
      },
      tooltip: {
        callbacks: {
          label: ctx => {
            const day = ctx.label;
            const obj = dataList.find(i => i.weekday_name === day);
            if (!obj) return "";
            return `Avg: ${obj.avg_person_count.toFixed(2)} — Hour: ${obj.hour}:00`;
          },
        },
      },
      datalabels: {
        anchor: "center",
        align: "center",
        color: theme.palette.getContrastText(alpha(theme.palette.primary.main, 0.6)),
        font: { weight: "bold", size: 12 },
        formatter: val => val.toFixed(2),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: { display: true, text: "Average Person Count", color: theme.palette.text.primary }
      },
      x: {
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: { display: true, text: "Weekday", color: theme.palette.text.primary }
      },
    },
  };

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />,
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton variant="rectangular" height="500px" sx={{ borderRadius: 2 }} />
      ) : (
        <Box sx={{ height: "500px" }}>
          <Bar data={data} options={options} />
        </Box>
      )}

      {/* ساعات الذروة تحت الأعمدة */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-around" }}>
        {hours.map((hr, i) => (
          <span key={i} style={{ fontSize: 14, color: theme.palette.primary.main }}>
            {hr}
          </span>
        ))}
      </Box>
    </Card>
  );
};

export default BusiestHourChart;
